import { Component, inject, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { EMPTY, Observable, tap } from 'rxjs';

import { EntityTypeConstants } from 'src/app/common/EntityTypeConstants';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { FieldErrorDirective } from '../form-row/field-errors.component';
import { FormRowComponent } from '../form-row/form-row.component';
import {
  DEFAULT_RECEIPT_WIDTH_CM,
  MAX_RECEIPT_WIDTH_CM,
  MIN_RECEIPT_WIDTH_CM,
  RECEIPT_WIDTH_SETTING,
  parseReceiptWidth,
  sampleReceiptOrder,
} from '../order-receipt/order-receipt';
import { OrderReceiptComponent } from '../order-receipt/order-receipt.component';
import { ReceiptPrinter } from '../order-receipt/receipt-printer.service';
import { SettingsPageStore } from './settings-page.store';

function buildPrinterForm(fb: FormBuilder) {
  return fb.group({
    posPrinterPageWidth: fb.control<string>(null, [
      Validators.required,
      Validators.min(MIN_RECEIPT_WIDTH_CM),
      Validators.max(MAX_RECEIPT_WIDTH_CM),
      Validators.pattern('^[0-9]*$'),
    ]),
  });
}

/**
 * #648: the receipt printer's paper width (System tab) — Customer-scoped, so the page shows it to
 * retailer admins only: only they can write it, and only retailers print receipts.
 *
 * #205: the width alone. A receipt is as long as its order, so the page height that used to sit
 * beside it (`PosPrinterPageHeight`) is no longer read or written; a stored row stays unused.
 * The test print prints a sample order at the width as typed, before it is saved.
 */
@Component({
  selector: 'app-printer-settings',
  imports: [ReactiveFormsModule, AsyncClickDirective, FormRowComponent, FieldErrorDirective, OrderReceiptComponent],
  templateUrl: './printer-settings.component.html',
})
export class PrinterSettingsComponent {
  private readonly store = inject(SettingsPageStore);
  private readonly printer = inject(ReceiptPrinter);

  readonly profile = this.store.profile;
  readonly form = buildPrinterForm(inject(FormBuilder));
  readonly sample = sampleReceiptOrder();
  private readonly receipt = viewChild.required(OrderReceiptComponent);

  constructor() {
    this.store.patchOnRead(EntityTypeConstants.EntityTypeCustomer, this.form, () =>
      this.form.patchValue({
        posPrinterPageWidth: this.store.value(EntityTypeConstants.EntityTypeCustomer, RECEIPT_WIDTH_SETTING),
      }),
    );
  }

  /** The width typed so far, or the default while it is not a valid one. */
  get previewWidthCm(): number {
    return parseReceiptWidth(this.form.controls.posPrinterPageWidth.value) ?? DEFAULT_RECEIPT_WIDTH_CM;
  }

  save(): Observable<unknown> {
    if (this.form.invalid) {
      return EMPTY;
    }
    const scope = EntityTypeConstants.EntityTypeCustomer;
    return this.store
      .save$([this.store.row(scope, RECEIPT_WIDTH_SETTING, this.form.getRawValue().posPrinterPageWidth)])
      .pipe(
        tap(() => {
          this.form.markAsPristine();
          this.store.reload(scope);
        }),
      );
  }

  onTestPrint(): void {
    this.printer.print(this.receipt().sheet().nativeElement, this.previewWidthCm).subscribe();
  }
}
